import { BLOCKS } from '@contentful/rich-text-types';
import React, { useRef } from 'react';

import BehavioralData from '../components/Animations/BehavioralData/BehavioralData';
import AnimatedContentCard from '../components/Common/AnimatedContentCard.js';
import ComparisonTable from '../components/Common/ComparisonTable';
import ContentCard from '../components/Common/ContentCard';
import ContentfulImage from '../components/Common/ContentfulImage';
import Drawer from '../components/Common/Drawer';
import FloatingCallout from '../components/Common/FloatingCallout';
import ImageBleedCallout from '../components/Common/ImageBleedCallout.js';
import { ArrowLink, PrimaryLink } from '../components/Common/Links';
import MarketoForm from '../components/Common/MarketoForm/MarketoForm';
import MasonryGrid from '../components/Common/MasonryGrid';
import Reviews from '../components/Common/Reviews';
import * as embeddedEntries from '../components/Common/RichText/EmbeddedEntries';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import RoiCalculator from '../components/Common/RoiCalculator/RoiCalculator.js';
import RotatingMedia from '../components/Common/RotatingMedia.js';
import ScrollAnimation from '../components/Common/ScrollAnimation.js';
import { scrollToFormTop } from '../components/Common/ScrollTo';
import Cards from '../components/Common/SideScroll/Cards.js';
import ManualSideScroll from '../components/Common/SideScroll/ManualSideScroll.js';
import SideScroll from '../components/Common/SideScroll/SideScroll.js';
import Slider from '../components/Common/Slider.js';
import SmartLink from '../components/Common/SmartLink';
import StylizedHeadlineCallout from '../components/Common/StylizedHeadlineCallout.js';
import TabbedContent from '../components/Common/TabbedContent';
import TabbedCustomerStories from '../components/Common/TabbedCustomerStories.js';
import Tabs from '../components/Common/Tabs.js';
import VisualBelowHeadline from '../components/Common/VisualBelowHeadline.js';
import ProgramContentCard from '../components/Generic/ProgramContentCard';
import AnimatedRow from '../components/HomePage/AnimatedRow.js';
import VideoBanner from '../components/HomePage/VideoBanner';
import AdditionalResources from '../components/PageSections/AdditionalResources.js';
import Carousel from '../components/PageSections/Carousel/Carousel';
import CenteredTextOverlay from '../components/PageSections/CenteredTextOverlay.js';
import FooterCallToAction from '../components/PageSections/FooterCallToAction';
import FullSectionCard from '../components/PageSections/FullSectionCard';
import G2Quote from '../components/PageSections/G2Quote.js';
import GroupedContent from '../components/PageSections/GroupedContent';
import HalfAndHalfContent from '../components/PageSections/HalfAndHalfContent';
import HalfBGContentCard from '../components/PageSections/HalfBGContentCard';
import List from '../components/PageSections/List';
import LogoSection from '../components/PageSections/LogoSection';
import PageSection from '../components/PageSections/PageSection';
import SessionList from '../components/PageSections/sessionList';
import Testimonial from '../components/PageSections/Testimonial.js';
import TestimonialLegacy from '../components/PageSections/TestimonialLegacy.js';
import ThreeUp from '../components/PageSections/ThreeUp';
import ZPattern from '../components/PageSections/ZPattern.js';
import { getThemeClass } from './textTools.js';

export const sectionBuilder = (
  page,
  styles,
  pageType = '',
  pageContext = {},
  scrollRef = { current: undefined },
  onCtaClick = () => null
) => {
  return page.pageSections?.map((section, i) => {
    const isCampaignLP = pageType === 'campaign';
    switch (section.__typename) {
      case 'ContentfulSectionIntersectionMarker': {
        if (section.featureType === 'Product Tour Violator') {
          return (
            <VideoBanner cta={section.callToAction} colorTheme={section.sectionColorTheme} offset={section.offset} />
          );
        }
        return null;
      }

      case 'ContentfulSectionTabbedContent': {
        return (
          <TabbedContent
            key={i}
            heading={section.mainHeading}
            lede={section.lede?.lede}
            content={section.tabbedContent}
          />
        );
      }

      case 'ContentfulSectionContentSection': {
        if (section.useFloatingLayout) {
          return <FloatingCallout key={i} section={section} />;
        }
        if (section.imageStyle === 'Half Content Section') {
          return (
            <HalfAndHalfContent key={i} section={section} extended={!section.imageBackgroundGradient && isCampaignLP} />
          );
        }
        if (section.titleIsSectionHeader) {
          let location = section.headingLocation === 'Left' ? styles.headingLeft : '';
          return (
            <PageSection
              slim
              className={[section.backgroundColor === 'Grey' ? styles.grey : '', styles.sectionHeaderWrap].join(' ')}
              key={`title-${i}`}
              data-analytics-component={'section-header'}
              data-analytics-id={section.title}
            >
              <div className={[styles.sectionHeader, location].join(' ')}>
                <h2>{section.title}</h2>
              </div>
            </PageSection>
          );
        }
        let imageSide = section.imageLocation === true ? 'Left' : 'Right';
        if (section.imageLocation === undefined) {
          imageSide = i > 0 && i % 2 === 0 ? 'Left' : 'Right';
        }

        switch (section.imageStyle) {
          case 'Small Centered': {
            return <ProgramContentCard key={i} section={section} imageSide={imageSide} limitImageSize={true} />;
          }
          case 'Centered With Background': {
            return <HalfBGContentCard key={i} section={section} imageSide={imageSide} slideIn />;
          }
          case 'Scroll-Animated': {
            return <AnimatedContentCard key={i} section={section} />;
          }
          case 'Below Content with Bleed': {
            // TODO: move this into it's own component
            return (
              <PageSection
                key={i}
                slim
                wide
                fullBleed
                data-analytics-component={'content-card-with-bleed'}
                data-analytics-id={section.title}
              >
                <div className={styles.stacked}>
                  <div className={styles.copy}>
                    <h2>{section.title}</h2>
                    <RichTextConverter copy={section.copy} />
                  </div>
                  {section.mainImage && (
                    <>
                      <ContentfulImage imageFragment={section.mainImage} alt={section.mainImage?.title} />
                    </>
                  )}
                </div>
              </PageSection>
            );
          }
          case 'Full Background': {
            // TODO: move this into it's own component
            return (
              <PageSection
                key={i}
                slim
                wide
                fullBleed
                className={[styles[section.backgroundColor], styles.fullBackground].join(' ')}
                data-analytics-component={'full-bg-content-card'}
                data-analytics-id={section.title}
              >
                {section.backgroundImage && (
                  <div
                    className={styles.backgroundImage}
                    style={{
                      '--backgroundImage': `url(${section.backgroundImage.file?.url})`,
                    }}
                  />
                )}
                <div className={styles.wrapper}>
                  <div className={styles.copy}>
                    {section.logo && <ContentfulImage lazy={true} imageFragment={section.logo} />}
                    <h3>{section.title}</h3>
                    {section.subtitle && <span className={styles.attribution}>{section.subtitle}</span>}
                    {section.ctaUrl && (
                      <ArrowLink className={styles.cta} url={section.ctaUrl} label={section.ctaLabel} />
                    )}
                  </div>
                  {section.mainImage &&
                    (section.ctaUrl ? (
                      <SmartLink className={styles.image} to={section.ctaUrl}>
                        <ContentfulImage
                          lazy={true}
                          imageFragment={section.mainImage}
                          height={section.mainImage.height}
                          width={section.mainImage.width}
                        />
                      </SmartLink>
                    ) : (
                      <div className={styles.image}>
                        <ContentfulImage
                          lazy={true}
                          imageFragment={section.mainImage}
                          height={section.mainImage.height}
                          width={section.mainImage.width}
                        />
                      </div>
                    ))}
                </div>
              </PageSection>
            );
          }
          case 'Centered Text Overlay': {
            return <CenteredTextOverlay section={section} key={i} />;
          }
          case 'Below Headline': {
            return <VisualBelowHeadline section={section} key={i} />;
          }
          default:
            break;
        }

        /*if (section.imageStyle === 'Half & Half') {
          return <HalfAndHalfContent section={section} imageSide={imageSide} />;
        }*/

        const backgroundColors = {
          White: styles.white,
          Grey: styles.grey,
          'Grey Gradient': styles.greyGradient,
          Blue: styles.blue,
          Purple: styles.purple,
          'Purple / Pink Gradient': styles.purplePinkGradient,
          Evergreen: styles.evergreen,
          Lilac: styles.lilac,
          'Lilac Deep': styles.lilacDeep,
        };

        return (
          <ContentCard
            key={i}
            slim
            backgroundImage={section.backgroundImage}
            anchorImage={section.imageStyle === 'Half & Half Image Anchored'}
            adjustForTallImage={section.adjustForTallImage}
            eyebrowText={section.subtitle}
            eyebrowColor={section.subtitleColor}
            heading={section.titleIsSectionHeader ? '' : section.title}
            image={section.mainImage}
            wistiaUrl={section.wistiaUrl}
            logo={section.logo}
            copy={section.copy}
            attachedContent={section.contentSection}
            ctaUrl={section.ctaUrl}
            ctaLabel={section.ctaLabel}
            newWindow={section.ctaOpensNewWindow}
            imageBleed={section.imageStyle === 'Bleed' || isCampaignLP}
            imageSide={imageSide}
            imageHeight={section.mainImage?.height}
            imageWidth={section.mainImage?.width}
            limitImageHeight
            className={[backgroundColors[section.backgroundColor] || ''].join(' ')}
            curve={section.hasCurve}
            animate={true}
            lazy={true}
            fullHeight={section.fullHeight}
          />
        );
      }
      case 'ContentfulSectionCallout': {
        if (section.style === 'Floating') {
          return <FloatingCallout key={i} section={section} />;
        } else if (section.style === 'Image Vertical Bleed') {
          return <ImageBleedCallout key={i} section={section} />;
        }
        return (
          <FullSectionCard
            key={i}
            label={section.ctaLabel}
            heading={section.title}
            backColor={section.backgroundOverlayColor}
            imageSrc={section.mainImage?.file?.url ? section.mainImage.file.url : ''}
            backImageSrc={section.backgroundImage ? section.backgroundImage.file.url : ''}
            link={section.ctaUrl}
            position={section.imagePosition}
            section={section}
          />
        );
      }
      case 'ContentfulComponentSimpleContent': {
        if (section.splitTitleAndBody) {
          return (
            <PageSection key={i} slim data-analytics-component={'simple-content'} data-analytics-id={section.title}>
              <div className={styles.simpleHalf}>
                <h2>{section.title}</h2>
                {section.body && <RichTextConverter copy={section.body} />}
              </div>
            </PageSection>
          );
        }
        return (
          <PageSection
            key={i}
            slim
            wide
            fullBleed
            className={section.textJustification === 'Left' ? styles.leftJustify : styles.centered}
            data-analytics-component={'simple-content'}
            data-analytics-id={section.title}
          >
            <h2>{section.title}</h2>
            {section.body && <RichTextConverter copy={section.body} />}
            {section.linkUrl && section.linkLabel && (
              <PrimaryLink isButton label={section.linkLabel} url={section.linkUrl} />
            )}
          </PageSection>
        );
      }
      case 'ContentfulJsonData': {
        return <SessionList section={section} />;
      }
      case 'ContentfulComponentTestimonial': {
        let bgColor;
        switch (section.backgroundColor) {
          case 'Grey': {
            bgColor = styles.grey;
            break;
          }
          case 'Light Blue': {
            bgColor = styles.blue;
            break;
          }
          case 'Light Purple': {
            bgColor = styles.purple;
            break;
          }
          default: {
            bgColor = styles.white;
            break;
          }
        }

        return (
          <PageSection key={i} slim className={bgColor} data-analytics-section={'legacy-testimonial'}>
            <TestimonialLegacy logoClass={styles.logo} logo={section.logo} body={section?.quote?.quote} />
          </PageSection>
        );
      }
      case 'ContentfulSectionMarketoForm': {
        // TODO: turn this into a component, such as MarketoFormSection
        const options = {
          renderNode: {
            [BLOCKS.EMBEDDED_ENTRY]: (node) => {
              const type = node?.data?.target?.internal?.type;
              if (type === 'ContentfulComponentMediaGroup') {
                return embeddedEntries.simpleMediaGroup(node, styles.logoGridSmall);
              }
            },
          },
        };

        const topOfFormRef = useRef(null);
        const onFormSuccess = async () => {
          scrollToFormTop(topOfFormRef);
        };

        let form = (
          <MarketoForm
            data-analytics-section={'form'}
            onSuccess={onFormSuccess}
            redirectUrl={section.onSuccessRedirectUrl}
            formId={section.marketoFormId}
            successMessage={section.formSuccessMessage}
            productOfInterest={section.productOfInterest}
          />
        );

        const backgroundColors = {
          Grey: styles.grey,
          White: styles.white,
          Evergreen: styles.evergreen,
          'Dark Blue Gradient': styles.darkBlueGradient,
          'Spark Pink Gradient': styles.sparkPinkGradient,
          'Multicolor Gradient': styles.multicolorGradient,
        };

        const themeClass = getThemeClass(section.colorTheme?.themeName);

        return (
          <>
            {page.headingCtaScrollsToForm && <div ref={scrollRef} id={'marketo-form'} />}
            <PageSection
              moderate
              backgroundImage={section.backgroundImage}
              className={[
                section.backgroundColor ? backgroundColors[section.backgroundColor] : styles.grey,
                themeClass,
              ].join(' ')}
              key={i}
              data-analytics-section={'form-wrapper'}
            >
              <div className={styles.form}>
                <div className={styles.formCopy} data-analytics-component={'form-copy'}>
                  <RichTextConverter copy={section?.formCopy} options={options} />
                </div>
                <div
                  data-analytics-component={'marketo-form'}
                  data-analytics-id={section.marketoFormId}
                  id={'mkto-form-wrap'}
                  ref={topOfFormRef}
                  className={section.backgroundColor === 'Dark Blue Gradient' ? styles.formWhite : ''}
                >
                  {form}
                </div>
              </div>
            </PageSection>
          </>
        );
      }
      case 'ContentfulSectionGroupedItems': {
        if (section.type === 'Logo List') {
          return <AnimatedRow key={i} heading={section.heading} logos={section.content} />;
        }
        // TODO: This is a confusing check and needs to be addressed
        // * Context: We currently have two styles of the Three Up module.
        // * We're using the check below to determine when to render the
        // * newer animated version with the gradient bg color.
        if (section.backgroundColor && section.type === 'Three Up') {
          let bgColor;
          switch (section.backgroundColor) {
            case 'Blue Gradient':
              bgColor = 'blueGradient';
              break;
            case 'Spark Gradient': {
              bgColor = 'sparkGradient';
              break;
            }
            case 'Purple Gradient': {
              bgColor = 'purpleGradient';
              break;
            }
            case 'Evergreen': {
              bgColor = 'evergreen';
              break;
            }
            default:
              break;
          }
          return <ThreeUp section={section} animate key={i} className={styles[bgColor]} />;
        }
        if (section.type === 'Comparison Table') {
          return <ComparisonTable key={i} section={section} />;
        }
        if (section.type === 'Hardcoded Animation') {
          return section.animationType === 'Behavioral Data' ? (
            <BehavioralData key={i} content={section.content} />
          ) : (
            <ScrollAnimation key={i} section={section} />
          );
        }
        if (section.type === 'Masonry Grid') {
          return (
            <MasonryGrid
              key={i}
              title={section.title}
              content={section.content}
              backgroundColor={section.backgroundColor}
            />
          );
        }
        if (section.type === 'Reviews') {
          return <Reviews key={i} content={section.content} backgroundColor={section.backgroundColor} />;
        }
        if (section.type === 'Side Scroll') {
          return section.scrollingContentType === 'cards' ? (
            <ManualSideScroll key={i}>
              <Cards content={section.content} cardIsClickable={false} />
            </ManualSideScroll>
          ) : (
            <SideScroll key={i} section={section} />
          );
        }
        if (section.type === 'Drawer') {
          return <Drawer key={i} section={section} />;
        }
        if (section.type === 'Tabbed Customer Stories') {
          return <TabbedCustomerStories key={i} section={section} />;
        }
        if (section.type === 'Slider') {
          return <Slider key={i} section={section} />;
        }
        if (section.type === 'Tabs') {
          return <Tabs key={i} section={section} />;
        }
        return (
          <GroupedContent
            key={i}
            section={section}
            i={i}
            noPad={isCampaignLP}
            wide={isCampaignLP}
            fullImage={isCampaignLP}
            forceNewTab={isCampaignLP}
            colorTheme={page.colorTheme}
          />
        );
      }

      case 'ContentfulSectionCarousel': {
        const themeClass = getThemeClass(section.colorTheme?.themeName);

        return (
          <PageSection
            key={i}
            moderate
            wide
            fullBleed
            className={[styles[section.backgroundColor], themeClass].join(' ')}
            data-analytics-section={'carousel-wrapper'}
          >
            <div className={styles.carousel}>
              {page.slug === 'platform/digital-experience-intelligence' ? (
                <List
                  heading={section.heading}
                  eyebrowText={section.eyebrowText}
                  accentColor={section.accentColor}
                  flipped={section.imageSide === 'true'}
                  items={section.cards}
                  onCta={onCtaClick}
                  ctaLabel={section.ctaLabel}
                  ctaUrl={section.ctaUrl}
                />
              ) : (
                <Carousel
                  heading={section.heading}
                  eyebrowText={section.eyebrowText}
                  accentColor={section.accentColor}
                  flipped={section.imageSide === 'true'}
                  items={section.cards}
                  onCta={onCtaClick}
                  ctaLabel={section.ctaLabel}
                  ctaUrl={section.ctaUrl}
                />
              )}
            </div>
          </PageSection>
        );
      }
      case 'ContentfulComponentCallToAction': {
        const backgroundColors = {
          Grey: styles.grey,
          'Dark Blue Circle Pattern': styles.darkBlue,
          White: styles.white,
          'Light Grey': styles.lightGrey,
          'Purple / Pink': styles.purplePink,
          'Purple Gradient': styles.purpleGradient,
          'Multicolor Gradient': styles.multicolorGradient,
        };

        const bgColor = backgroundColors[section.backgroundColor];

        let fullSection = [];

        if (!section.image && !section.attachment) {
          return (
            <PageSection key={i} className={backgroundColors[section.backgroundColor] || styles.white}>
              <FooterCallToAction section={section} />
            </PageSection>
          );
        }
        // TODO: Is this being used?
        fullSection.push(
          <ContentCard
            key={i}
            heading={section.title}
            backgroundStyle={bgColor || styles.white}
            image={section.image}
            copy={section?.description?.description}
            imageBleed={false}
            imageSide={'Right'}
            ctaUrl={section.url || section?.attachment?.file?.url}
            ctaIsDownload={section?.attachment?.file?.url || false}
            ctaLabel={section.ctaLabel}
            newWindow={section.newWindow}
            limitImageHeight
            moderate
          />
        );
        return fullSection;
      }
      case 'ContentfulHalfContentSection': {
        return (
          <HalfAndHalfContent key={i} section={section} extended={!section.imageBackgroundGradient && isCampaignLP} />
        );
      }
      case 'ContentfulComponentMediaGroup': {
        if (section.type === 'Rotating') {
          return <RotatingMedia key={i} section={section} />;
        } else {
          return <LogoSection key={i} section={section} />;
        }
      }

      case 'ContentfulSectionThreeUp': {
        return <ThreeUp center={section.centeredTitle} section={section} animate key={i} />;
      }

      case 'ContentfulSectionG2Quote': {
        return <G2Quote section={section} key={i} />;
      }

      case 'ContentfulSectionZPattern': {
        return <ZPattern section={section} key={i} />;
      }

      case 'ContentfulSectionTestimonial': {
        return <Testimonial section={section} key={i} />;
      }

      case 'ContentfulSectionAdditionalResources': {
        return <AdditionalResources section={section} key={i} showImage={section.showImages} />;
      }

      case 'ContentfulEmbedJumpLink': {
        return <div key={i} id={section.sectionId} style={{ height: 0 }} />;
      }

      case 'ContentfulComponentRoiCalculator': {
        return <RoiCalculator key={i} section={section} />;
      }

      case 'ContentfulSectionStylizedHeadlineCallout': {
        return <StylizedHeadlineCallout key={i} section={section} />;
      }

      default: {
        return '';
      }
    }
  });
};
